import { appendQueryParams, getInitialsFromName, showToast, useHubSwitch } from '@bpm-web-app/utils';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Media, SearchPaginatedQuery } from '@bpm-web-app/api-client';
import classNames from 'classnames';
import { LibraryTabsContext } from 'libs/utils/src/lib/library-tabs.context';
import styles from './artist-tooltip.module.css';
import { AppLink } from '../app-link/app-link';
import useFollowArtist from '../three-dots-sheet/useFollowArtist';

const BANNER_TILE_COUNT = 3;

export function ArtistTooltipContent({ contentObj, onHide, onReady }: { contentObj: string, onHide: () => void, onReady: (ready: boolean) => void }) {
    const { isDownload } = useHubSwitch();
    const { libraryProperty } = useContext(LibraryTabsContext);

    const [artistName, artistSlug, artistId, artistImageUrl] = contentObj.split('|');

    const { isArtistFollowed, unfollowArtist, followArtist } = useFollowArtist(parseInt(artistId, 10));

    const [tracksCount, setTracksCount] = useState<number>();
    const [artworkUrls, setArtworkUrls] = useState<string[]>([]);

    const query = useMemo<SearchPaginatedQuery>(() => {
        const queryParams: SearchPaginatedQuery = {
            artist: artistSlug?.toString(),
            library: libraryProperty,
            limit: BANNER_TILE_COUNT,
        };
        return queryParams;
    }, [artistSlug, libraryProperty]);

    const handleResetTooltipValues = () => {
        onReady(false);
        setTracksCount(undefined);
        setArtworkUrls([]);
    };

    useEffect(() => {
        handleResetTooltipValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artistId]);

    useEffect(() => {
        if (artistSlug && query) {
            const searchAlbums = Media.searchAlbum(isDownload, query);
            searchAlbums.then((result) => {
                if (result.pagination.total) {
                    setTracksCount(result.pagination.total);
                    setArtworkUrls(result.data.map((album) => album.cover_url));
                    onReady(true);
                }
                return result;
            }).catch(() => {
                showToast({ type: 'error', title: 'Error fetching artist tracks' });
            });
        }
    }, [artistSlug, isDownload, query, onReady]);

    const renderLoading = () => {
        return (
            <div className={styles['artist-tooltip--loading']}>
                <div className={styles['artist-tooltip-ellipsis']}>
                    <span className={styles['artist-tooltip-ellipsis-dot']} />
                    <span className={styles['artist-tooltip-ellipsis-dot']} />
                    <span className={styles['artist-tooltip-ellipsis-dot']} />
                </div>
                Tracks
            </div>
        );
    };

    return (
        <div className={styles['artist-tooltip-container']}>
            <div className={styles['artist-tooltip']}>
                <div className={styles['artist-tooltip__banner-tiles']}>
                    {Array.from({ length: BANNER_TILE_COUNT }, (_, index) => (
                        <div
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            className={classNames(styles['artist-tooltip__banner-tile'], { [styles['artist-tooltip__banner-tile--center']]: index === 1 })}>
                            {artworkUrls[index] && (
                                <img
                                    src={appendQueryParams(artworkUrls[index], { key: 'dw', value: 78 })}
                                    srcSet={`${appendQueryParams(artworkUrls[index], { key: 'dw', value: 156 })} 2x`}
                                    alt=""
                                    draggable={false}
                                />
                            )}
                        </div>
                    ))}
                </div>
                <div className={styles['artist-tooltip__image-container']}>
                    <figure className={styles['artist-tooltip__image']}>
                        {
                            !artistImageUrl || artistImageUrl === null || artistImageUrl === '' || artistImageUrl === 'null' ? (
                                <div className={styles['artist-tooltip__image-initials']}>{getInitialsFromName(artistName)}</div>
                            ) : (
                                <img
                                    src={appendQueryParams(artistImageUrl, { key: 'dw', value: 48 })}
                                    srcSet={`${appendQueryParams(artistImageUrl, { key: 'dw', value: 96 })} 2x`}
                                    alt={artistName}
                                    draggable={false}
                                />
                            )
                        }
                    </figure>
                </div>
                <div className={styles['artist-tooltip__text-container']}>
                    <AppLink href={`/artist/${artistSlug}`} key={artistId}>
                        <button type="button" onClick={onHide} className={classNames('underline-link', styles['artist-tooltip__name'])}>{artistName}</button>
                    </AppLink>
                    <div className={styles['artist-tooltip__tracks-count']}>
                        {tracksCount !== undefined ? (
                            <>
                                <span className={styles['artist-tooltip__tracks-count-number']}>{tracksCount.toLocaleString()}</span>
                                {' '}
                                {tracksCount === 1 ? 'Track' : 'Tracks'}
                            </>
                        ) : renderLoading()}
                    </div>
                </div>
                <div className={styles['artist-tooltip__button-container']}>
                    <button
                        type="button"
                        className={styles['artist-tooltip__follow-button']}
                        onClick={isArtistFollowed ? unfollowArtist : followArtist}>
                        <span>{isArtistFollowed ? 'Followed' : 'Follow'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
